import Debug from 'debug';
import * as React from 'react';
import { Spinner, Stack, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { Entity, Field, getFieldArray, getListRowLink, getRowKey, Option, QueryOptions, Row } from 'gossamer-unified';
import { ErrorBoundary } from '../ancillary/ErrorBoundary';
import { updateListManagerWithRows } from '../ancillary/ListManager';
import { SubSectionMultiProps } from '../data/QueryMulti';
import { ChecksGroup } from '../fields/ChecksGroup';
import { ListCell, ListColumnHeader } from './List';
import stylesList from './List.module.css';
import styles from './GroupedList.module.css';
import { General } from '../fields/General';
import { useQueryOptionsChanges } from '../data/useQueryOptions';
import { useCacheQuery } from '../data/useCacheQuery';
import { useCloneEntity } from '../ancillary/useCloneEntity';

const debug = Debug('gfe/se/GroupedList');

interface GroupedListWrapperProps<T extends Row> {
  alterEntity?: (localEntity: Entity<T>) => void;
  entity: Entity<T>;
  initialQueryOptions: QueryOptions;
  initialGroupLevel?: number;
  lockColumnSorting?: boolean;
  lockLevelGrouping?: boolean;
}

export const GroupedListWrapper = <T extends Row>(props: GroupedListWrapperProps<T>): React.JSX.Element => {
  const LocalEntity = useCloneEntity(props.entity, props.alterEntity || (() => {}));
  const [queryOptions, updateQueryOptions] = useQueryOptionsChanges(LocalEntity, props.initialQueryOptions);
  const query = useCacheQuery(LocalEntity, queryOptions);
  return (
    <>
      <GroupedList
        entity={LocalEntity}
        rows={query.data || []}
        queryOptions={queryOptions}
        updateQueryOptions={updateQueryOptions}
        initialGroupLevel={props.initialGroupLevel}
        lockColumnSorting={props.lockColumnSorting}
        lockLevelGrouping={props.lockLevelGrouping}
      />
      {query.isFetching && <Spinner animation="border" size="sm" />}
      {query.error && <div>{query.error}</div>}
    </>
  );
};

interface GroupedListProps<T extends Row> extends SubSectionMultiProps<T> {
  initialGroupLevel?: number;
  lockColumnSorting?: boolean;
  lockLevelGrouping?: boolean;
}

export const GroupedList: React.FC<GroupedListProps<any>> = (props) => {
  const navigate = useNavigate();
  const [groupLevel, setGroupLevel] = React.useState<number>(
    typeof props.initialGroupLevel === 'number' ? props.initialGroupLevel : -1,
  ); // -1 = off, 0 = 1st sorted col, 1 = 2nd, etc
  if (groupLevel > -1 && !props.queryOptions?.order) {
    throw new Error(`query has no order defined`);
  }
  const maxLevel = props.queryOptions?.order?.length || 0;
  if (groupLevel < -1 || groupLevel > maxLevel) {
    throw new Error(`invalid groupLevel: ${groupLevel}, maxLevel: ${maxLevel}`);
  }
  React.useEffect(() => console.log('GroupedList remount'), []);
  const [unfolded, setUnfolded] = React.useState<string[]>([]);
  const isUnfolded = (foldKey: string) => unfolded.indexOf(foldKey) > -1;
  const renderFoldControl = (foldKey: string, isUnfoldedHere: boolean) => (
    <div>
      <button
        className="ControlIcon foldControl"
        type="button"
        onClick={() => {
          const newArray = unfolded.slice();
          if (isUnfoldedHere) {
            newArray.splice(newArray.indexOf(foldKey), 1);
          } else {
            newArray.push(foldKey);
          }
          debug(`clicking fold icon`, foldKey, isUnfoldedHere, unfolded, newArray);
          setUnfolded(newArray);
        }}
      >
        ▶
      </button>
    </div>
  );

  const isRowLinks = !!props.entity.listRowLink || !!props.onRowClick;
  const handleRowLinkClick = (key: string) => {
    if (props.onRowClick) {
      props.onRowClick(key);
    } else if (props.entity.listRowLink) {
      if (props.rowClickSetsSearchPaths) {
        updateListManagerWithRows(props.entity, props.rows);
      }
      navigate(getListRowLink(props.entity.listRowLink, key));
    }
  };

  const groupLevels = (props.queryOptions?.order || []).map((sort) => sort[0]).slice(0, groupLevel + 1);
  const groupLevelOptions = [{ id: '-1', label: 'Off' }].concat(
    (props.queryOptions?.order || []).map((sort, index) => ({
      id: String(index),
      label: props.entity.fields[sort[0]].label,
    })),
  );
  const shownColumns = getFieldArray(props.entity).filter(
    ([field, fieldId]: [Field, string]) => field.listColumn && groupLevels.indexOf(fieldId) === -1,
  );
  debug('groupLevel calcs:', groupLevel, groupLevels, groupLevelOptions, unfolded);
  const children: React.JSX.Element[] = [];
  const breakValues: any[] = [];

  let isUnfoldedToLevel = 0;

  props.rows
    .filter((row) => !props.excludeValues || props.excludeValues.indexOf(getRowKey(props.entity, row)) === -1)
    .forEach((row: any) => {
      const breakToLevel = groupLevels.reduce((prev, fieldId, index) => {
        if (prev > -1) {
          return prev;
        }
        if (index > groupLevel) {
          return prev;
        }
        if (breakValues.length <= index || breakValues[index] !== row[fieldId]) {
          return index;
        }
        return prev;
      }, -1);

      // set break values
      for (let i = breakToLevel; i > -1 && i < groupLevels.length; i += 1) {
        const fieldId = groupLevels[i];
        const value = row[fieldId];
        breakValues[i] = value;
      }

      // render break header rows
      for (let i = breakToLevel; i > -1 && i < groupLevels.length && i <= isUnfoldedToLevel; i += 1) {
        const fieldId = groupLevels[i];
        const field = props.entity.fields[fieldId];
        const value = row[fieldId];
        const foldKey = 'fold-key|' + breakValues.slice(0, i + 1).join('|');
        const isUnfoldedHere = isUnfolded(foldKey);
        const className = styles.BreakHeaderRow + (isUnfoldedHere ? ' unfolded' : ' folded');
        children.push(
          <tr key={foldKey} id={foldKey}>
            {i > 0 && <td colSpan={i} key="indent_cell" />}
            <td colSpan={shownColumns.length + groupLevels.length - i} key="main_cell">
              <div className={className}>
                {renderFoldControl(foldKey, isUnfoldedHere)}
                <div>{field.label}:</div>
                <div>
                  <General fieldId={fieldId} field={field} value={value} editable={false} />
                </div>
              </div>
            </td>
          </tr>,
        );
        isUnfoldedToLevel = isUnfoldedHere ? i + 1 : i;
      }
      if (isUnfoldedToLevel < groupLevels.length) {
        return;
      }
      const key = getRowKey(props.entity, row);
      const className = isRowLinks ? stylesList.RowLink : undefined;
      children.push(
        <tr key={key} onClick={() => handleRowLinkClick(key)} className={className}>
          {groupLevels.length > 0 && <td colSpan={groupLevels.length} />}
          {shownColumns.map(([field, fieldId]: [Field, string]) => (
            <ListCell key={fieldId} field={field} fieldId={fieldId} isRowLinks={isRowLinks} row={row} />
          ))}
        </tr>,
      );
    });

  if (props.rows.length === 0) {
    return <div>no data</div>;
  }
  return (
    <ErrorBoundary>
      <Table responsive bordered hover={isRowLinks}>
        <thead>
          <tr>
            {groupLevels.map((fieldId) => (
              <th className={styles.BreakColumnHeader} key={fieldId} />
            ))}
            {shownColumns.map(([field, fieldId]: [Field, string]) => (
              <ListColumnHeader
                key={fieldId}
                entity={props.entity}
                field={field}
                fieldId={fieldId}
                isSortable={!props.lockColumnSorting}
                queryOptions={props.queryOptions}
                updateQueryOptions={props.updateQueryOptions}
              />
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </Table>
      {!props.lockLevelGrouping && (
        <LevelGroupSelector
          groupLevel={String(groupLevel)}
          groupLevelOptions={groupLevelOptions}
          setGroupLevel={(newValue: string) => setGroupLevel(parseFloat(newValue))}
        />
      )}
    </ErrorBoundary>
  );
};

interface LevelGroupSelectorProps {
  groupLevel: string;
  groupLevelOptions: Option[];
  setGroupLevel: (newLevel: string) => void;
}

const LevelGroupSelector = (props: LevelGroupSelectorProps) => (
  <Stack direction="horizontal" gap={3} className="mb10px">
    <div style={{ width: 120 }}>Group Level:</div>
    <ChecksGroup
      inline={true}
      mandatory={true}
      type="checkbox"
      options={props.groupLevelOptions}
      onItemSelected={props.setGroupLevel}
      value={props.groupLevel}
    />
  </Stack>
);
